import { useCatalog, isResponsive } from "catalog/Catalog.context";
import {
  CatalogLink,
  CatalogAutoplayButton,
  CatalogPagingButton,
} from "catalog/CatalogNavigations";
import { AnimatePresence } from "framer-motion";
import { Anime, Image } from "components/elements";
import { useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

export default function GlobalNavigation() {
  return (
    <div className="pointer-events-none absolute left-0 top-0 z-[9999] h-full w-full">
      <Header />
      <Controls />
      <LangNav />
      <LnbBar />
    </div>
  );
}

function Header() {
  const { pageName, nestedPageName } = useParams();
  const { activePageInfo, isResponsive } = useCatalog();
  if (!activePageInfo) return null;

  const { gnb, depth1 } = activePageInfo;

  const classNames = {
    buttonContainer: twMerge("relative z-10 flex flex-shrink-0 flex-col items-center mo:w-[50px]"),
    button: twMerge(
      "flex h-[72px] w-[50px] items-center justify-center",
      "mo:w-[50px] mo:h-[50px]",
    ),
    buttonDivider: twMerge("h-[1px] w-[46px] bg-[#707070] block"),
    gnb: {
      container: twMerge("h-full w-full bg-[#4E484A] flex flex-col items-center mo:hidden"),
      button: (active) =>
        twMerge(
          "flex items-center justify-center w-full h-full uppercase text-white font-secondary text-[21px] writing-mode:vertical-lr",
          "hover:text-opacity-50",
          depth1 === active && "font-bold",
          depth1 === active && gnb.theme === "blue"
            ? "bg-tt-blue"
            : depth1 === active && gnb.theme === "red"
              ? "bg-tt-red"
              : "bg-transparent",
        ),
      buttonText: "rotate-90",
    },
  };

  return (
    <header
      className={twMerge(
        "pointer-events-auto relative z-[888] flex h-full w-[70px] flex-col items-center justify-between",
        "mo:fixed mo:h-[50px] mo:w-[50px] mo:w-full mo:items-start mo:border-b-[1px] mo:border-[#e1e1e1] mo:bg-[#fff] ",
      )}
    >
      <AnimatePresence>
        {gnb && (
          <Anime
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            className={twMerge("absolute left-0 top-0 z-0 h-full w-full bg-[#4E484A] mo:hidden")}
          />
        )}
      </AnimatePresence>
      <div className={classNames.buttonContainer}>
        <CatalogLink to="/" className={classNames.button}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="28.999"
            height="31.024"
            viewBox="0 0 28.999 31.024"
            className="pointer-events-auto mo:h-[20px] mo:w-[20px]"
          >
            <path
              d="M4223.559-7289.745h-9.441v8.769h-6.069v-14.163H4204L4218.5-7312l14.5,16.861h-3.371v14.162h-6.07Z"
              transform="translate(-4204.001 7312.001)"
              fill={pageName === "last" || isResponsive ? "#A7A7A7" : "#fff"}
            />
          </svg>
        </CatalogLink>
        <i className={twMerge(classNames.buttonDivider, "mo:hidden")} />
        <CatalogAutoplayButton
          className={twMerge(classNames.button, "mo:hidden")}
          children={{
            play: (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="35"
                height="35"
                viewBox="0 0 36 36"
                className="pointer-events-none mo:h-[20px] mo:w-[20px]"
              >
                <path
                  d="M4222-7276a17.88,17.88,0,0,1-12.727-5.272A17.883,17.883,0,0,1,4204-7294a17.884,17.884,0,0,1,5.271-12.728A17.88,17.88,0,0,1,4222-7312a17.883,17.883,0,0,1,12.729,5.272A17.882,17.882,0,0,1,4240-7294a17.88,17.88,0,0,1-5.272,12.728A17.883,17.883,0,0,1,4222-7276Zm-5-26h0v17l14-8.5-14-8.5Z"
                  transform="translate(-4204.001 7312)"
                  fill={pageName === "last" ? "#a7a7a7" : "#fff"}
                />
              </svg>
            ),
            pause: (
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="29" viewBox="0 0 24 29">
                <rect width="10" height="29" fill="#fff" />
                <rect width="10" height="29" transform="translate(14)" fill="#fff" />
              </svg>
            ),
          }}
        />
      </div>

      <AnimatePresence>
        {gnb && (
          <Anime
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            className={classNames.gnb.container}
          >
            <CatalogLink to="company-about" className={classNames.gnb.button("company")}>
              <span className={classNames.gnb.buttonText}>Company</span>
            </CatalogLink>
            <i className={classNames.buttonDivider} />
            <CatalogLink to="solution-intro" className={classNames.gnb.button("solution")}>
              <span className={classNames.gnb.buttonText}>Solution</span>
            </CatalogLink>
            <i className={classNames.buttonDivider} />
            <CatalogLink to="technology-core" className={classNames.gnb.button("technology")}>
              <span className={classNames.gnb.buttonText}>Technology</span>
            </CatalogLink>
            <i className={classNames.buttonDivider} />
            <CatalogLink to="service-one-stop" className={classNames.gnb.button("service")}>
              <span className={classNames.gnb.buttonText}>Service</span>
            </CatalogLink>
          </Anime>
        )}
      </AnimatePresence>

      <div className={twMerge(classNames.buttonContainer, "mo:hidden")}>
        <CatalogLink to="/cover/index" className={classNames.button}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="28"
            height="22"
            viewBox="0 0 28 22"
            className="mo:w-[20px]"
          >
            <rect
              width="28"
              height="4"
              fill={
                (pageName === "cover" && nestedPageName === "index") ||
                pageName === "last" ||
                isResponsive
                  ? "#A7A7A7"
                  : "#fff"
              }
            />
            <rect
              width="28"
              height="4"
              transform="translate(0 9)"
              fill={
                (pageName === "cover" && nestedPageName === "index") ||
                pageName === "last" ||
                isResponsive
                  ? "#A7A7A7"
                  : "#fff"
              }
            />
            <rect
              width="28"
              height="4"
              transform="translate(0 18)"
              fill={
                (pageName === "cover" && nestedPageName === "index") ||
                pageName === "last" ||
                isResponsive
                  ? "#A7A7A7"
                  : "#fff"
              }
            />
          </svg>
        </CatalogLink>
      </div>
    </header>
  );
}

function LangNav() {
  const { lang } = useParams();
  const { pageName, nestedPageName } = useParams();

  const { activePageInfo, isResponsive } = useCatalog();
  if (!activePageInfo) return null;

  const classNames = {
    container: twMerge(`
      lang-nav absolute right-[26px] top-[26px] flex items-center gap-[.5em] font-secondary text-[21px] text-[#9F9F9F]",
      ${nestedPageName === "index" && "bg-[#9F9F9F]"}`),
    buttonActive: (ln) =>
      twMerge(
        "pointer-events-auto flex items-center gap-[0.5em] bg-[#E6E6E6] rounded-[35px] px-[.6em] py-[.2em]",
        !isResponsive && lang === ln && "font-bold text-white bg-[#0084FF]",
        !isResponsive &&
          lang === ln &&
          activePageInfo?.langNavTheme === "white" &&
          "text-[#626262] bg-[#fff]",
        isResponsive && (lang === ln ? "bg-[#0084FF] text-[#fff]" : "bg-[#E6E6E6]"),
      ),
    divider: twMerge("h-[1em] w-[1px] bg-[#C8C8C8]"),
  };

  return (
    <nav
      className={twMerge(
        "lang-nav absolute right-[26px] top-[26px] flex items-center gap-[.5em] font-secondary text-[21px] text-[#9F9F9F]",
        "mo:fixed mo:right-[10px] mo:top-[12px] mo:z-[999] mo:text-[13px]",
      )}
    >
      <CatalogLink to="ko/cover/cover" absolute className={classNames.buttonActive("ko")}>
        <Image src="lang-ko.png" className="mo:w-[15px]"></Image>KOR
      </CatalogLink>
      {/* <i className={classNames.divider}></i> */}
      <CatalogLink to="en/cover/cover" absolute className={classNames.buttonActive("en")}>
        <Image src="lang-en.png" className="mo:w-[15px]"></Image>ENG
      </CatalogLink>
    </nav>
  );
}

function Controls() {
  const { pageName, nestedPageName } = useParams();
  const { activePageInfo, isResponsive } = useCatalog();
  if (!activePageInfo) return null;

  const { navi } = activePageInfo;

  const classNames = {
    container: twMerge(
      "absolute bottom-0 left-0 flex h-[72px] w-full justify-between pl-[70px] *:pointer-events-auto",
      "mo:pl-[50px] mo:h-[50px] mo:bg-[#fff] mo:pl-[0px]",
    ),
    button: twMerge(
      "flex h-[72px] w-[72px] items-center justify-center text-xl font-bold disabled:opacity-50",
      "mo:w-[50px] mo:h-[50px]",
    ),
  };

  return (
    <nav className={twMerge(classNames.container, "mo:fixed mo:z-[999]")}>
      <CatalogLink to="/cover/index" className={twMerge(classNames.button, "hidden", "mo:flex")}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28"
          height="22"
          viewBox="0 0 28 22"
          className="mo:w-[20px]"
        >
          <rect
            width="28"
            height="4"
            fill={
              (pageName === "cover" && nestedPageName === "index") ||
              pageName === "last" ||
              isResponsive
                ? "#A7A7A7"
                : "#fff"
            }
          />
          <rect
            width="28"
            height="4"
            transform="translate(0 9)"
            fill={
              (pageName === "cover" && nestedPageName === "index") ||
              pageName === "last" ||
              isResponsive
                ? "#A7A7A7"
                : "#fff"
            }
          />
          <rect
            width="28"
            height="4"
            transform="translate(0 18)"
            fill={
              (pageName === "cover" && nestedPageName === "index") ||
              pageName === "last" ||
              isResponsive
                ? "#A7A7A7"
                : "#fff"
            }
          />
        </svg>
      </CatalogLink>
      <div className={twMerge("navi", "flex w-full justify-between")}>
        <CatalogPagingButton direction="prev" className={classNames.button}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="21.474"
            height="31.695"
            viewBox="0 0 21.474 31.695"
            className="mo:h-[20px] mo:w-[20px]"
          >
            <path
              d="M21.474,15.762,6.865,1.154a3.939,3.939,0,0,0-5.571,5.57l9.055,9.054-9.2,9.195a3.939,3.939,0,0,0,5.57,5.57l14.75-14.75-.015-.015Z"
              transform="translate(21.474 31.695) rotate(180)"
              fill={
                nestedPageName === "index" ||
                navi?.theme === "dark" ||
                navi?.left === "dark" ||
                isResponsive
                  ? "#9F9F9F"
                  : "#fff"
              }
            />
          </svg>
        </CatalogPagingButton>
        <CatalogPagingButton direction="next" className={classNames.button}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="21.474"
            height="31.697"
            viewBox="0 0 21.474 31.697"
            className="mo:h-[20px] mo:w-[20px]"
          >
            <path
              d="M0,15.763,14.61,1.154a3.939,3.939,0,1,1,5.571,5.57l-9.055,9.054,9.2,9.195a3.939,3.939,0,1,1-5.57,5.57L0,15.793l.015-.015Z"
              transform="translate(21.474 31.697) rotate(180)"
              fill={
                nestedPageName === "index" ||
                navi?.theme === "dark" ||
                navi?.right === "dark" ||
                isResponsive
                  ? "#9F9F9F"
                  : "#fff"
              }
            />
          </svg>
        </CatalogPagingButton>
      </div>
    </nav>
  );
}

function LnbBar() {
  const { pageName, nestedPageName } = useParams();
  const { activePageInfo, lang } = useCatalog();

  if (!activePageInfo) return null;

  const { depth1, depth2, depth3, depth4, lnb } = activePageInfo;
  if (lang === "ko") {
    return (
      <nav
        className={twMerge(
          "lnb-bar *:pointer-events-auto",
          lnb?.state
            ? "lnb-hide"
            : lnb?.theme === "dark"
              ? "lnb-dark"
              : lnb?.theme === "white"
                ? "lnb-white"
                : "",
        )}
      >
        <div className={`lnb-item tt-u ${depth1 === "company" ? "active" : ""}`}>
          <CatalogLink
            to="company-about"
            className={`lnb-btn ${depth2 === "about" ? "active" : ""}`}
          >
            About THOTH
          </CatalogLink>
          <CatalogLink
            to="company-differntiation"
            className={`lnb-btn ${depth2 === "differntiation" ? "active" : ""}`}
          >
            Differntiation
          </CatalogLink>
          <CatalogLink
            to="company-competitive"
            className={`lnb-btn ${depth2 === "competitive" ? "active" : ""}`}
          >
            Competitive
          </CatalogLink>
          <CatalogLink
            to="company-business"
            className={`lnb-btn ${depth2 === "business" ? "active" : ""}`}
          >
            Business Area
          </CatalogLink>
        </div>

        <div className={`lnb-item ${depth2 === "recycling" ? "active" : ""}`}>
          <CatalogLink
            to="solution-recycling-overview"
            className={`lnb-btn ${depth3 === "overview" ? "active" : ""}`}
          >
            솔루션 개요
          </CatalogLink>
          <div className="submenu-wrap">
            <CatalogLink
              to="solution-recycling-dismantlebot-intro"
              className={`lnb-btn ${depth3 === "dismantlebot" ? "active" : ""}`}
            >
              DisMantleBot
            </CatalogLink>
            <div className={twMerge("submenu", depth3 === "dismantlebot" ? "active" : "")}>
              <CatalogLink
                to="solution-recycling-dismantlebot-intro"
                className={twMerge("submenu-btn", depth4 === "intro" ? "active" : "")}
              >
                제품소개
              </CatalogLink>
              <CatalogLink
                to="solution-recycling-dismantlebot-lineup"
                className={twMerge("submenu-btn", depth4 === "lineup" ? "active" : "")}
              >
                제품라인업
              </CatalogLink>
              <CatalogLink
                to="solution-recycling-dismantlebot-custom/1"
                className={twMerge("submenu-btn", depth4 === "custom" ? "active" : "")}
              >
                커스텀(고객요구 맞춤 조합)
              </CatalogLink>
            </div>
          </div>
        </div>

        <div className={`lnb-item ${depth2 === "logistics" ? "active" : ""}`}>
          <CatalogLink
            to="solution-logistics-overview"
            className={`lnb-btn ${depth3 === "overview" ? "active" : ""}`}
          >
            솔루션 개요
          </CatalogLink>
          <div className="submenu-wrap">
            <CatalogLink
              to="solution-logistics-flexipacker-intro"
              className={`lnb-btn ${depth3 === "flexipacker" ? "active" : ""}`}
            >
              FlexiPacker
            </CatalogLink>
            <div className={twMerge("submenu", depth3 === "flexipacker" ? "active" : "")}>
              <CatalogLink
                to="solution-logistics-flexipacker-intro"
                className={twMerge("submenu-btn", depth4 === "intro" ? "active" : "")}
              >
                제품소개
              </CatalogLink>
              <CatalogLink
                to="solution-logistics-flexipacker-module"
                className={twMerge("submenu-btn", depth4 === "module" ? "active" : "")}
              >
                모듈식 구성 요소
              </CatalogLink>
              <CatalogLink
                to="solution-logistics-flexipacker-automation/1"
                className={twMerge("submenu-btn", depth4 === "automation" ? "active" : "")}
              >
                물류 완전 자동화
              </CatalogLink>
            </div>
          </div>
          <div className="submenu-wrap">
            <CatalogLink
              to="solution-logistics-fruitpacker-intro"
              className={`lnb-btn ${depth3 === "fruitpacker" ? "active" : ""}`}
            >
              FruitPacker
            </CatalogLink>
            <div className={twMerge("submenu", depth3 === "fruitpacker" ? "active" : "")}>
              <CatalogLink
                to="solution-logistics-fruitpacker-intro"
                className={twMerge("submenu-btn", depth4 === "intro" ? "active" : "")}
              >
                제품소개
              </CatalogLink>
              <CatalogLink
                to="solution-logistics-fruitpacker-module/1"
                className={twMerge("submenu-btn", depth4 === "module" ? "active" : "")}
              >
                모듈 조합
              </CatalogLink>
              <CatalogLink
                to="solution-logistics-fruitpacker-lineup"
                className={twMerge("submenu-btn", depth4 === "lineup" ? "active" : "")}
              >
                제품라인업
              </CatalogLink>
            </div>
          </div>
        </div>

        <div className={`lnb-item ${depth2 === "custom" ? "active" : ""}`}>
          <CatalogLink
            to="solution-custom-overview"
            className={`lnb-btn ${depth3 === "overview" ? "active" : ""}`}
          >
            솔루션 개요
          </CatalogLink>
          <CatalogLink
            to="solution-custom-application"
            className={`lnb-btn ${depth3 === "application" ? "active" : ""}`}
          >
            적용 분야
          </CatalogLink>
        </div>

        <div className={`lnb-item technology ${depth1 === "technology" ? "active" : ""}`}>
          <CatalogLink
            to="technology-core"
            className={`lnb-btn ${depth2 === "core" ? "active" : ""}`}
          >
            CORE TECHNOLOGY
          </CatalogLink>
          <div className="submenu-wrap">
            <CatalogLink
              to="technology-raaps-tech"
              className={`lnb-btn ${depth2 === "raaps" ? "active" : ""}`}
            >
              RAAPS
            </CatalogLink>
            <div className={twMerge("submenu", depth2 === "raaps" ? "active" : "")}>
              <CatalogLink
                to="technology-raaps-tech"
                className={twMerge("submenu-btn", depth3 === "tech" ? "active" : "")}
              >
                4세대 공정 자동화 기술
              </CatalogLink>
              <CatalogLink
                to="technology-raaps-solution"
                className={twMerge("submenu-btn", depth3 === "solution" ? "active solution" : "")}
              >
                자율 프로그래밍 로봇 AI 솔루션
              </CatalogLink>
            </div>
          </div>
        </div>

        <div className={`lnb-item service tt-u ${depth1 === "service" ? "active" : ""}`}>
          <CatalogLink
            to="service-one-stop"
            className={`lnb-btn ${depth2 === "one-stop" ? "active" : ""}`}
          >
            원스톱 솔루션
          </CatalogLink>
          <CatalogLink
            to="service-provide"
            className={`lnb-btn ${depth2 === "provide" ? "active" : ""}`}
          >
            제공 서비스
          </CatalogLink>
          <CatalogLink
            to="service-center"
            className={`lnb-btn ${depth2 === "center" ? "active center" : ""}`}
          >
            거점 센터 서비스
          </CatalogLink>
        </div>
      </nav>
    );
  } else {
    return (
      <nav
        className={twMerge(
          "lnb-bar *:pointer-events-auto",
          lnb?.state
            ? "lnb-hide"
            : lnb?.theme === "dark"
              ? "lnb-dark"
              : lnb?.theme === "white"
                ? "lnb-white"
                : "",
        )}
      >
        <div className={`lnb-item tt-u ${depth1 === "company" ? "active" : ""}`}>
          <CatalogLink
            to="company-about"
            className={`lnb-btn ${depth2 === "about" ? "active" : ""}`}
          >
            About THOTH
          </CatalogLink>
          <CatalogLink
            to="company-differntiation"
            className={`lnb-btn ${depth2 === "differntiation" ? "active" : ""}`}
          >
            Differntiation
          </CatalogLink>
          <CatalogLink
            to="company-competitive"
            className={`lnb-btn ${depth2 === "competitive" ? "active" : ""}`}
          >
            Competitive
          </CatalogLink>
          <CatalogLink
            to="company-business"
            className={`lnb-btn ${depth2 === "business" ? "active" : ""}`}
          >
            Business Area
          </CatalogLink>
        </div>

        <div className={`lnb-item ${depth2 === "recycling" ? "active" : ""}`}>
          <CatalogLink
            to="solution-recycling-overview"
            className={`lnb-btn ${depth3 === "overview" ? "active" : ""}`}
          >
            Solution Overview
          </CatalogLink>
          <div className="submenu-wrap">
            <CatalogLink
              to="solution-recycling-dismantlebot-intro"
              className={`lnb-btn ${depth3 === "dismantlebot" ? "active" : ""}`}
            >
              DisMantleBot
            </CatalogLink>
            <div className={twMerge("submenu", depth3 === "dismantlebot" ? "active" : "")}>
              <CatalogLink
                to="solution-recycling-dismantlebot-intro"
                className={twMerge("submenu-btn", depth4 === "intro" ? "active" : "")}
              >
                Product Overview
              </CatalogLink>
              <CatalogLink
                to="solution-recycling-dismantlebot-lineup"
                className={twMerge("submenu-btn", depth4 === "lineup" ? "active" : "")}
              >
                Product Line-up
              </CatalogLink>
              <CatalogLink
                to="solution-recycling-dismantlebot-custom/1"
                className={twMerge("submenu-btn", depth4 === "custom" ? "active" : "")}
              >
                Custom <br />
                <small className="block leading-4">
                  (Tailored solutions based on <br /> customer requirements)
                </small>
              </CatalogLink>
            </div>
          </div>
        </div>

        <div className={`lnb-item ${depth2 === "logistics" ? "active" : ""}`}>
          <CatalogLink
            to="solution-logistics-overview"
            className={`lnb-btn ${depth3 === "overview" ? "active" : ""}`}
          >
            Solution Overview
          </CatalogLink>
          <div className="submenu-wrap">
            <CatalogLink
              to="solution-logistics-flexipacker-intro"
              className={`lnb-btn ${depth3 === "flexipacker" ? "active" : ""}`}
            >
              FlexiPacker
            </CatalogLink>
            <div className={twMerge("submenu", depth3 === "flexipacker" ? "active" : "")}>
              <CatalogLink
                to="solution-logistics-flexipacker-intro"
                className={twMerge("submenu-btn", depth4 === "intro" ? "active" : "")}
              >
                Product Overview
              </CatalogLink>
              <CatalogLink
                to="solution-logistics-flexipacker-module"
                className={twMerge("submenu-btn", depth4 === "module" ? "active" : "")}
              >
                Modular Components
              </CatalogLink>
              <CatalogLink
                to="solution-logistics-flexipacker-automation/1"
                className={twMerge("submenu-btn", depth4 === "automation" ? "active" : "")}
              >
                Full Automation for Packaging
              </CatalogLink>
            </div>
          </div>
          <div className="submenu-wrap">
            <CatalogLink
              to="solution-logistics-fruitpacker-intro"
              className={`lnb-btn ${depth3 === "fruitpacker" ? "active" : ""}`}
            >
              FruitPacker
            </CatalogLink>
            <div className={twMerge("submenu", depth3 === "fruitpacker" ? "active" : "")}>
              <CatalogLink
                to="solution-logistics-fruitpacker-intro"
                className={twMerge("submenu-btn", depth4 === "intro" ? "active" : "")}
              >
                Product Overview
              </CatalogLink>
              <CatalogLink
                to="solution-logistics-fruitpacker-module/1"
                className={twMerge("submenu-btn", depth4 === "module" ? "active" : "")}
              >
                Modular Components
              </CatalogLink>
              <CatalogLink
                to="solution-logistics-fruitpacker-lineup"
                className={twMerge("submenu-btn", depth4 === "lineup" ? "active" : "")}
              >
                Product Line-up
              </CatalogLink>
            </div>
          </div>
        </div>

        <div className={`lnb-item ${depth2 === "custom" ? "active" : ""}`}>
          <CatalogLink
            to="solution-custom-overview"
            className={`lnb-btn ${depth3 === "overview" ? "active" : ""}`}
          >
            Solution Overview
          </CatalogLink>
          <CatalogLink
            to="solution-custom-application"
            className={`lnb-btn ${depth3 === "application" ? "active" : ""}`}
          >
            Application Field
          </CatalogLink>
        </div>

        <div className={`lnb-item technology ${depth1 === "technology" ? "active" : ""}`}>
          <CatalogLink
            to="technology-core"
            className={`lnb-btn ${depth2 === "core" ? "active" : ""}`}
          >
            CORE TECHNOLOGY
          </CatalogLink>
          <div className="submenu-wrap">
            <CatalogLink
              to="technology-raaps-tech"
              className={`lnb-btn ${depth2 === "raaps" ? "active" : ""}`}
            >
              RAAPS
            </CatalogLink>
            <div className={twMerge("submenu", depth2 === "raaps" ? "active" : "")}>
              <CatalogLink
                to="technology-raaps-tech"
                className={twMerge("submenu-btn leading-6", depth3 === "tech" ? "active" : "")}
              >
                Fourth-Generation Process <br />
                Automation Technology
              </CatalogLink>
              <CatalogLink
                to="technology-raaps-solution"
                className={twMerge(
                  "submenu-btn leading-6",
                  depth3 === "solution" ? "active solution" : "",
                )}
              >
                Robot AI Solutions <br />
                That Enable Self-Programming
              </CatalogLink>
            </div>
          </div>
        </div>

        <div className={`lnb-item service ${depth1 === "service" ? "active" : ""}`}>
          <CatalogLink
            to="service-one-stop"
            className={`lnb-btn ${depth2 === "one-stop" ? "active" : ""}`}
          >
            One Stop Solution
          </CatalogLink>
          <CatalogLink
            to="service-provide"
            className={`lnb-btn ${depth2 === "provide" ? "active" : ""}`}
          >
            Services Offered
          </CatalogLink>
          <CatalogLink
            to="service-center"
            className={`lnb-btn ${depth2 === "center" ? "active center" : ""}`}
          >
            Hub Center Service
          </CatalogLink>
        </div>
      </nav>
    );
  }
}
